import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle, 
  FolderPlus, 
  RefreshCw, 
  Users, 
  Image as ImageIcon,
  HardDrive,
  X,
  Clock,
  ChevronRight,
  Trash2,
  Zap,
  ArrowRight,
  ShieldCheck
} from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import { useLogo } from '../../contexts/LogoContext';
import { useAuth } from '../../contexts/AuthContext';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';

const PhotoRecognitionManagement = ({ isStandalone = false, user = null }) => {
  const { logoUrl } = useLogo();
  const { currentUser } = useAuth();
  const activeUser = user || currentUser;
  const isAdminUser = !isStandalone && (activeUser?.role === 'admin' || activeUser?.isAdmin === true);

  const [albums, setAlbums] = useState([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState('');
  const [photographerName, setPhotographerName] = useState(
    activeUser?.displayName || `${activeUser?.firstName || ''} ${activeUser?.lastName || ''}`.trim() || activeUser?.email?.split('@')[0] || ''
  );
  const [isLoading, setIsLoading] = useState(false);

  // New Album Modal State
  const [isNewAlbumModalOpen, setIsNewAlbumModalOpen] = useState(false);
  const [newAlbumTitle, setNewAlbumTitle] = useState('');
  const [newAlbumDescription, setNewAlbumDescription] = useState('');
  const [newAlbumDate, setNewAlbumDate] = useState(new Date().toISOString().split('T')[0]);
  const [isCreatingAlbum, setIsCreatingAlbum] = useState(false);

  // Bulk Upload State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
  const [indexedFacesCount, setIndexedFacesCount] = useState(0);
  const [currentStepText, setCurrentStepText] = useState('');
  const [isExtractingZip, setIsExtractingZip] = useState(false);
  const [zipExtractProgress, setZipExtractProgress] = useState('');

  // Live Speed, ETA & Thumbnail Stream State
  const [uploadSpeed, setUploadSpeed] = useState('');
  const [uploadEta, setUploadEta] = useState('');
  const [uploadedDataFormatted, setUploadedDataFormatted] = useState('');
  const [recentThumbnails, setRecentThumbnails] = useState([]);
  const [uploadSuccessSummary, setUploadSuccessSummary] = useState(null);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const zipInputRef = useRef(null);

  // Accidental Tab-Close Protection during large uploads
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = 'A batch photo upload is currently in progress. Leaving will interrupt remaining uploads.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/photos/albums');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.albums) {
          setAlbums(data.albums);
          if (data.albums.length > 0 && !selectedAlbumId) {
            setSelectedAlbumId(data.albums[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching albums:', err);
      toast.error('Failed to load photo albums.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    if (!newAlbumTitle.trim()) {
      toast.error('Please enter an album title');
      return;
    }

    try {
      setIsCreatingAlbum(true);
      const res = await api.post('/photos/albums', {
        title: newAlbumTitle.trim(),
        description: newAlbumDescription.trim(),
        eventDate: newAlbumDate
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.album) {
          toast.success(`Album "${data.album.title}" created & AI collection initialized!`);
          setIsNewAlbumModalOpen(false);
          setNewAlbumTitle('');
          setNewAlbumDescription('');
          await fetchAlbums();
          setSelectedAlbumId(data.album.id);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.error || 'Failed to create album');
      }
    } catch (err) {
      console.error('Error creating album:', err);
      toast.error('Failed to create album');
    } finally {
      setIsCreatingAlbum(false);
    }
  };

  const handleDeleteAlbum = async (e, album) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete album "${album.title}"?\n\nThis will permanently delete the album metadata and its AWS Rekognition face index.`)) {
      return;
    }

    try {
      const res = await api.delete(`/photos/albums/${album.id}`);
      if (res.ok) {
        toast.success(`Album "${album.title}" and its AI face index were deleted.`);
        if (selectedAlbumId === album.id) {
          setSelectedAlbumId('');
        }
        await fetchAlbums();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || 'Failed to delete album');
      }
    } catch (err) {
      console.error('Error deleting album:', err);
      toast.error('Failed to delete album');
    }
  };

  const processSelectedFiles = async (rawFiles) => {
    const directImages = [];
    const zipFiles = [];

    for (const file of rawFiles) {
      if (file.name.toLowerCase().endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
        zipFiles.push(file);
      } else if (file.type.startsWith('image/') || /\.(jpg|jpeg|png|webp|heic)$/i.test(file.name)) {
        directImages.push(file);
      }
    }

    let allExtractedImages = [...directImages];

    if (zipFiles.length > 0) {
      setIsExtractingZip(true);
      for (const zipFile of zipFiles) {
        try {
          setZipExtractProgress(`Extracting photos from ${zipFile.name}...`);
          const zip = await JSZip.loadAsync(zipFile);
          const zipEntries = Object.values(zip.files).filter(entry => 
            !entry.dir && 
            !entry.name.startsWith('__MACOSX') && 
            !entry.name.startsWith('.') &&
            /\.(jpg|jpeg|png|webp|heic)$/i.test(entry.name)
          );

          setZipExtractProgress(`Found ${zipEntries.length} photos in ${zipFile.name}. Unpacking...`);

          for (const entry of zipEntries) {
            const blob = await entry.async('blob');
            const cleanName = entry.name.split('/').pop();
            const extractedFile = new File([blob], cleanName, { type: blob.type || 'image/jpeg' });
            allExtractedImages.push(extractedFile);
          }
        } catch (zipErr) {
          console.error(`Error reading zip file ${zipFile.name}:`, zipErr);
          toast.error(`Failed to unpack zip: ${zipFile.name}`);
        }
      }
      setIsExtractingZip(false);
      setZipExtractProgress('');
    }

    let processedImages = [];
    if (allExtractedImages.length > 0) {
      setIsExtractingZip(true);
      
      for (let i = 0; i < allExtractedImages.length; i++) {
        const file = allExtractedImages[i];
        
        // 1. Strict blocking of Mac hidden files and system files
        if (file.name.includes('/._') || file.name.startsWith('._') || file.name.includes('__MACOSX')) continue;
        
        let currentFile = file;
        setZipExtractProgress(`Optimizing ${i + 1} of ${allExtractedImages.length}...`);

        try {
          // 2. Convert HEIC/HEIF to JPEG
          if (/\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic') {
            // JSZip blobs might lack the correct mime type, which crashes heic2any
            const heicBlob = new Blob([file], { type: 'image/heic' });
            const convertedBlob = await heic2any({ 
              blob: heicBlob, 
              toType: 'image/jpeg', 
              quality: 0.8 
            });
            const blobToUse = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            currentFile = new File([blobToUse], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
          }

          // 3. Compress if larger than 8MB
          if (currentFile.size > 8 * 1024 * 1024) {
            const options = {
              maxSizeMB: 8,
              maxWidthOrHeight: 4096,
              useWebWorker: true,
              fileType: 'image/jpeg'
            };
            const compressedBlob = await imageCompression(currentFile, options);
            currentFile = new File([compressedBlob], currentFile.name, { type: 'image/jpeg' });
          }
          
          processedImages.push(currentFile);
        } catch (optErr) {
          console.warn(`Failed to process/optimize ${file.name}, uploading original:`, optErr);
          // NEVER discard the file. If HEIC conversion fails, just upload the original HEIC.
          processedImages.push(file);
        }
      }
      
      setIsExtractingZip(false);
      setZipExtractProgress('');
    }

    if (processedImages.length === 0) {
      toast.error('No valid image files found in selection or ZIP archive. Videos are currently ignored.');
      return;
    }

    setSelectedFiles(processedImages);
    toast.success(`Ready to upload ${processedImages.length.toLocaleString()} optimized photos!`);
  };

  const handleFilesSelected = (e) => {
    const rawFiles = Array.from(e.target.files || []);
    processSelectedFiles(rawFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const rawFiles = Array.from(e.dataTransfer.files || []);
    processSelectedFiles(rawFiles);
  };

  // Multi-chunk parallel direct upload to S3 + AI indexing
  const startBulkUpload = async () => {
    if (!selectedAlbumId) {
      toast.error('Please select an event album');
      return;
    }
    if (!photographerName.trim()) {
      toast.error('Please enter the Photographer Name for credits');
      return;
    }
    if (selectedFiles.length === 0) {
      toast.error('Please select photos or a folder to upload');
      return;
    }

    setIsUploading(true);
    setUploadSuccessSummary(null);
    setTotalFilesToUpload(selectedFiles.length);
    setProcessedCount(0);
    setIndexedFacesCount(0);
    setUploadProgress(0);
    setRecentThumbnails([]);
    setUploadSpeed('Calculating...');
    setUploadEta('Calculating...');

    // Calculate total bytes
    const totalBytes = selectedFiles.reduce((acc, f) => acc + (f.size || 0), 0);
    const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
    let cumulativeUploadedBytes = 0;
    const uploadStartTime = Date.now();

    const BATCH_SIZE = 25; // Process 25 files at a time
    let uploadedCount = 0;
    let facesIndexedTotal = 0;
    const collectedThumbs = [];

    try {
      for (let i = 0; i < selectedFiles.length; i += BATCH_SIZE) {
        const fileBatch = selectedFiles.slice(i, i + BATCH_SIZE);
        const batchBytes = fileBatch.reduce((acc, f) => acc + (f.size || 0), 0);
        const currentBatchNum = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(selectedFiles.length / BATCH_SIZE);

        setCurrentStepText(`Uploading batch ${currentBatchNum} of ${totalBatches}...`);

        // 1. Get presigned upload URLs for this batch
        const presignRes = await api.post('/photos/get-presigned-batch-urls', {
          albumId: selectedAlbumId,
          photographerName: photographerName.trim(),
          files: fileBatch.map(f => ({ name: f.name, type: f.type || 'image/jpeg' }))
        });

        const presignData = presignRes.ok ? await presignRes.json() : null;
        const presignedList = presignData?.presignedUrls || [];

        // 2. Upload batch concurrently directly to S3
        const uploadPromises = fileBatch.map((file, idx) => {
          const presigned = presignedList[idx];
          if (!presigned || !presigned.uploadUrl) return Promise.resolve(null);

          return fetch(presigned.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'image/jpeg' }
          }).then(res => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return {
              photoKey: presigned.photoKey,
              photoUrl: presigned.photoUrl
            };
          }).catch(err => {
            console.warn(`Upload failed for ${file.name}:`, err);
            return null;
          });
        });

        const uploadedResults = (await Promise.all(uploadPromises)).filter(Boolean);

        // Update byte count, speed, and ETA metrics
        cumulativeUploadedBytes += batchBytes;
        const elapsedSeconds = Math.max(1, (Date.now() - uploadStartTime) / 1000);
        const currentSpeedBytesPerSec = cumulativeUploadedBytes / elapsedSeconds;
        const speedMbps = (currentSpeedBytesPerSec / (1024 * 1024)).toFixed(1);
        const uploadedMB = (cumulativeUploadedBytes / (1024 * 1024)).toFixed(1);

        setUploadSpeed(`${speedMbps} MB/s`);
        setUploadedDataFormatted(`${uploadedMB} MB / ${totalMB} MB`);

        const remainingBytes = Math.max(0, totalBytes - cumulativeUploadedBytes);
        const remainingSeconds = Math.round(remainingBytes / (currentSpeedBytesPerSec || 1));
        if (remainingSeconds < 60) {
          setUploadEta(`~${remainingSeconds}s remaining`);
        } else {
          const mins = Math.ceil(remainingSeconds / 60);
          setUploadEta(`~${mins} min${mins > 1 ? 's' : ''} remaining`);
        }

        // Collect latest thumbnails for preview strip
        uploadedResults.slice(0, 4).forEach(item => {
          if (item && item.photoUrl && !collectedThumbs.some(t => t.photoKey === item.photoKey)) {
            collectedThumbs.unshift(item);
          }
        });
        setRecentThumbnails([...collectedThumbs.slice(0, 6)]);

        // 3. Trigger face indexing for this batch
        if (uploadedResults.length > 0) {
          setCurrentStepText(isAdminUser 
            ? `AI Indexing faces in ${uploadedResults.length} photos...` 
            : `Processing batch ${currentBatchNum} of ${totalBatches}...`
          );
          try {
            const indexRes = await api.post('/photos/index-batch', {
              albumId: selectedAlbumId,
              photographerName: photographerName.trim(),
              photos: uploadedResults
            });

            if (indexRes.ok) {
              const indexData = await indexRes.json();
              if (indexData?.facesIndexed) {
                facesIndexedTotal += indexData.facesIndexed;
                setIndexedFacesCount(facesIndexedTotal);
              }
            }
          } catch (indexErr) {
            console.warn('Batch indexing error:', indexErr);
          }
        }

        uploadedCount += fileBatch.length;
        setProcessedCount(uploadedCount);
        setUploadProgress(Math.round((uploadedCount / selectedFiles.length) * 100));
      }

      setUploadSuccessSummary({
        totalPhotos: selectedFiles.length,
        albumTitle: selectedAlbum?.title || 'Selected Album',
        photographerName: photographerName.trim(),
        facesIndexed: facesIndexedTotal
      });

      toast.success(`🎉 Successfully uploaded ${selectedFiles.length.toLocaleString()} photos!`);
      setSelectedFiles([]);
      await fetchAlbums();
    } catch (err) {
      console.error('Bulk upload error:', err);
      toast.error('An error occurred during bulk upload. Please check network and retry.');
    } finally {
      setIsUploading(false);
      setCurrentStepText('');
    }
  };

  const selectedAlbum = albums.find(a => a.id === selectedAlbumId);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-purple-950 to-orange-950 p-6 sm:p-8 text-white shadow-2xl">
        {/* Purple & Orange Glow in corners */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/25 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-600/20 rounded-full blur-2xl pointer-events-none -ml-20 -mb-20"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2.5">
            {isAdminUser ? (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-semibold text-purple-200 shadow-xs">
                <Camera className="w-3.5 h-3.5 text-purple-300" />
                <span>AI Media & Facial Recognition</span>
              </div>
            ) : (
              <div className="inline-flex flex-wrap items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs text-purple-100 shadow-xs">
                <span className="inline-flex items-center gap-1.5 text-green-300 font-bold">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                  <span>Authorized Contributor</span>
                </span>
                <span className="text-white/40">&bull;</span>
                <span className="text-gray-200">
                  Signed in as <span className="font-bold text-white">{activeUser?.displayName || activeUser?.email}</span>
                </span>
              </div>
            )}

            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              {isAdminUser ? 'AI Facial Recognition Portal' : 'Photographer Batch Upload Portal'}
            </h1>
            <p className="text-sm text-gray-300 max-w-xl">
              {isAdminUser 
                ? 'Manage event albums, monitor photographer contributions, and view AI face indexing stats.'
                : 'Select an active event album below to bulk upload raw event photo batches (4k–6k photos), SD card folders, or .ZIP archives.'
              }
            </p>
          </div>

          {/* Action Buttons & Circular Refresh Logo */}
          <div className="flex items-center gap-3.5">
            {!isAdminUser && (
              <Link
                to="/find-my-photos"
                className="px-4 py-2.5 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold transition-all hover:scale-105 shadow-md flex items-center gap-1.5"
              >
                <span>Public Face Search</span>
                <ArrowRight className="w-3.5 h-3.5 text-orange-300" />
              </Link>
            )}

            {isAdminUser && (
              <button
                onClick={() => setIsNewAlbumModalOpen(true)}
                className="px-4 sm:px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all hover:scale-105 shadow-md"
              >
                <FolderPlus className="w-4 h-4 text-orange-300" />
                <span>New Event Album</span>
              </button>
            )}

            {/* Circular BOW Logo as Refresh Button */}
            <button
              onClick={() => fetchAlbums()}
              className="group relative flex items-center justify-center p-1 rounded-full hover:ring-4 hover:ring-purple-400/40 transition-all duration-300 transform active:scale-95 flex-shrink-0"
              title="Click logo to refresh albums"
              aria-label="Refresh albums"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden ring-4 ring-white/40 shadow-2xl relative bg-white flex items-center justify-center">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="BOW Logo Refresh"
                    className={`w-full h-full object-cover rounded-full ${isLoading ? 'animate-spin' : 'group-hover:scale-110'} transition-transform duration-300`}
                  />
                ) : (
                  <Camera className={`w-8 h-8 text-purple-600 ${isLoading ? 'animate-spin' : ''}`} />
                )}
              </div>
              {/* Tooltip badge */}
              <span className="absolute -bottom-6 text-[11px] text-purple-200 opacity-0 group-hover:opacity-100 transition-opacity font-semibold whitespace-nowrap">
                Click to Refresh
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Album Selector & Stats Card (Admin Only) */}
      {isAdminUser && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-8 bg-white rounded-3xl p-6 sm:p-7 border border-gray-200/90 shadow-xs flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center font-bold shadow-xs">
                    <ImageIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 leading-tight">
                      Select Active Event Album
                    </h2>
                    <p className="text-xs text-gray-500">
                      Choose which event album photos will be uploaded to
                    </p>
                  </div>
                </div>

                <button 
                  onClick={fetchAlbums} 
                  disabled={isLoading}
                  className="text-xs font-semibold text-gray-500 hover:text-orange-600 flex items-center gap-1.5 px-3 py-1.5 rounded-xl hover:bg-orange-50 border border-gray-200/80 transition-colors"
                  title="Refresh Albums"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>

              {albums.length === 0 && !isLoading ? (
                <div className="py-12 text-center text-gray-400 bg-gray-50/60 rounded-2xl border border-dashed border-gray-200">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-semibold text-gray-600">No event albums found</p>
                  {isAdminUser && (
                    <button
                      type="button"
                      onClick={() => setIsNewAlbumModalOpen(true)}
                      className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-600 text-white text-xs font-bold shadow-sm hover:bg-orange-700 transition-colors"
                    >
                      <FolderPlus className="w-3.5 h-3.5" />
                      <span>Create Your First Album</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {albums.map((album) => {
                    const isSelected = album.id === selectedAlbumId;
                    return (
                      <div
                        key={album.id}
                        onClick={() => !isUploading && setSelectedAlbumId(album.id)}
                        className={`cursor-pointer rounded-2xl p-5 border-2 transition-all duration-200 relative flex flex-col justify-between ${
                          isSelected
                            ? 'border-orange-500 bg-gradient-to-br from-orange-50/90 via-amber-50/40 to-white shadow-md ring-4 ring-orange-500/15'
                            : 'border-gray-200/90 hover:border-orange-300 hover:shadow-sm bg-white'
                        }`}
                      >
                        <div>
                          {/* Top Meta Row */}
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                              isSelected 
                                ? 'bg-orange-600 text-white shadow-xs' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {isSelected ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" />
                                  <span>Selected Target</span>
                                </>
                              ) : (
                                <span>Click to Select</span>
                              )}
                            </span>

                            <div className="flex items-center gap-1">
                              {isAdminUser && (
                                <button
                                  type="button"
                                  onClick={(e) => handleDeleteAlbum(e, album)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title={`Delete album "${album.title}"`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Album Title */}
                          <h3 className="font-extrabold text-gray-900 text-base leading-snug mb-1.5">
                            {album.title}
                          </h3>

                          {/* Description */}
                          <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed mb-4">
                            {album.description || 'Public event photo collection.'}
                          </p>
                        </div>

                        {/* Bottom Footer Info */}
                        <div className="flex items-center justify-between text-xs text-gray-600 border-t border-gray-200/80 pt-3 mt-auto">
                          <span className="font-medium text-gray-500 flex items-center gap-1">
                            <span>📅</span>
                            <span>{album.eventDate || 'Recent Event'}</span>
                          </span>
                          <span className="font-bold text-orange-700 bg-orange-100/80 border border-orange-200/60 px-2.5 py-0.5 rounded-lg">
                            📸 {(album.totalPhotos || 0).toLocaleString()} Photos
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Live Album Stats / Overview Card */}
          <div className="lg:col-span-4 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-950 rounded-3xl p-6 sm:p-7 text-white shadow-md flex flex-col justify-between border border-gray-800">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold text-orange-400 uppercase tracking-wider mb-3">
                <Camera className="w-4 h-4 text-orange-400" />
                <span>AI Face Index Status</span>
              </div>

              <h3 className="text-xl font-extrabold truncate mb-5 text-white">
                {selectedAlbum?.title || 'No Album Selected'}
              </h3>

              <div className="space-y-3.5">
                <div className="flex justify-between items-center text-sm border-b border-gray-800 pb-2.5">
                  <span className="text-gray-400 font-medium">Total Uploaded Photos:</span>
                  <span className="font-extrabold text-base text-white">
                    {(selectedAlbum?.totalPhotos || 0).toLocaleString()}
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-sm border-b border-gray-800 pb-2.5">
                  <span className="text-gray-400 font-medium">Faces Indexed by AI:</span>
                  <span className="font-extrabold text-base text-yellow-400">
                    {(selectedAlbum?.totalFacesIndexed || 0).toLocaleString()}
                  </span>
                </div>

                <div className="flex flex-col gap-2 text-sm border-b border-gray-800 pb-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Active Contributors:</span>
                    <span className="font-bold text-orange-400">
                      {selectedAlbum?.photographers?.length || 0} photographer(s)
                    </span>
                  </div>
                  {selectedAlbum?.photographers && selectedAlbum.photographers.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {selectedAlbum.photographers.map((name, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/30"
                        >
                          📸 {name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500 italic">No photographer uploads yet</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-3 text-[11px] text-gray-400 flex items-center justify-between border-t border-gray-800">
              <div className="flex items-center gap-1.5 truncate">
                <HardDrive className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                <span className="truncate">Collection: {selectedAlbum?.rekognitionCollectionId || 'Ready'}</span>
              </div>
              <span className="text-gray-500 text-[10px]">Auto-Synced</span>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Uploader Box */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-200/90 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2.5">
              <UploadCloud className="w-6 h-6 text-orange-600" />
              <span>Batch Media Uploader (SD Cards & Folders)</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Bulk upload raw event photo batches (4,000–6,000 photos), SD card folders, or .ZIP archives. Files stream directly in parallel without browser freezing.
            </p>
          </div>
          <div className="flex-shrink-0">
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200/80">
              ⚡ Multi-threaded Direct S3 Upload
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Photographer / Contributor Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={photographerName}
              onChange={(e) => setPhotographerName(e.target.value)}
              placeholder="e.g. Rahul K. (Main Stage)"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium focus:ring-2 focus:ring-orange-500 focus:border-orange-500 shadow-xs"
              disabled={isUploading}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Target Event Album
            </label>
            <select
              value={selectedAlbumId}
              onChange={(e) => setSelectedAlbumId(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-semibold text-gray-800 shadow-xs"
              disabled={isUploading}
            >
              {albums.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title} ({a.eventDate || 'Active'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Success Summary Card */}
        {uploadSuccessSummary && !isUploading && (
          <div className="mb-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-green-950 via-slate-900 to-emerald-950 text-white border border-green-500/30 shadow-xl relative overflow-hidden animate-slide-up">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none -mr-16 -mt-16" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-green-500/20 text-green-400 border border-green-500/30 flex items-center justify-center flex-shrink-0 shadow-inner">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs font-bold uppercase tracking-wider mb-1 border border-green-500/30">
                    <span>Batch Ingestion Completed</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-white">
                    {uploadSuccessSummary.totalPhotos.toLocaleString()} Photos Successfully Uploaded!
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-300 mt-1">
                    Event: <span className="text-orange-300 font-semibold">{uploadSuccessSummary.albumTitle}</span> &bull; Contributor: <span className="text-white font-semibold">{uploadSuccessSummary.photographerName}</span>
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setUploadSuccessSummary(null)}
                  className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/20 transition-all shadow-sm"
                >
                  Upload Another Batch
                </button>
                <Link
                  to="/find-my-photos"
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-xs font-bold shadow-md hover:scale-105 transition-all flex items-center gap-1.5"
                >
                  <span>Test Public Face Search</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Dropzone & Action Tiles */}
        {!isUploading ? (
          <div 
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-orange-300/80 bg-gradient-to-b from-orange-50/40 to-amber-50/20 hover:bg-orange-50/70 rounded-3xl p-8 sm:p-12 lg:p-14 text-center transition-all duration-200"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFilesSelected}
              multiple
              accept="image/*,.zip,application/zip"
              className="hidden"
            />
            <input
              type="file"
              ref={folderInputRef}
              onChange={handleFilesSelected}
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
            />
            <input
              type="file"
              ref={zipInputRef}
              onChange={handleFilesSelected}
              accept=".zip,application/zip,application/x-zip-compressed"
              multiple
              className="hidden"
            />

            <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-orange-100 flex items-center justify-center mx-auto mb-4 text-orange-600">
              <UploadCloud className="w-8 h-8" />
            </div>

            <h3 className="text-base sm:text-lg font-extrabold text-gray-900 mb-1">
              Drag & Drop Photos, Folders, or .ZIP Archives Here
            </h3>
            
            {/* Upload Guidelines Box */}
            <div className="bg-orange-50/50 border border-orange-200 rounded-xl p-4 max-w-lg mx-auto mb-8 text-left mt-3">
              <div className="font-bold text-orange-900 text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                Upload Guidelines
              </div>
              <ul className="text-xs text-gray-700 space-y-1.5 ml-1">
                <li><strong className="text-gray-900">✅ Formats Allowed:</strong> Standard JPG or PNG photos only.</li>
                <li><strong className="text-gray-900">❌ Not Allowed:</strong> No Videos (.mov, .mp4) and no Apple HEIC files.</li>
                <li><strong className="text-gray-900">📦 File Size:</strong> Each photo must be strictly under 8 MB.</li>
              </ul>
            </div>

            {isExtractingZip ? (
              <div className="py-6 text-center max-w-sm mx-auto bg-white rounded-2xl p-6 border border-orange-200 shadow-xs">
                <RefreshCw className="w-8 h-8 animate-spin text-orange-600 mx-auto mb-3" />
                <p className="text-sm font-bold text-gray-900 mb-1">Unpacking ZIP Archive</p>
                <p className="text-xs font-semibold text-orange-700">{zipExtractProgress || 'Extracting photos...'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <button
                  type="button"
                  onClick={() => zipInputRef.current?.click()}
                  className="group p-5 rounded-2xl bg-white hover:bg-orange-600 border border-gray-200 hover:border-orange-600 text-gray-900 hover:text-white shadow-xs hover:shadow-lg transition-all duration-200 flex flex-col items-center text-center cursor-pointer"
                >
                  <span className="text-2xl mb-2 group-hover:scale-110 transition-transform">📦</span>
                  <span className="font-bold text-sm">Upload .ZIP Archive</span>
                  <span className="text-[11px] text-gray-500 group-hover:text-orange-100 mt-1">Full event zip file</span>
                </button>

                <button
                  type="button"
                  onClick={() => folderInputRef.current?.click()}
                  className="group p-5 rounded-2xl bg-white hover:bg-amber-600 border border-gray-200 hover:border-amber-600 text-gray-900 hover:text-white shadow-xs hover:shadow-lg transition-all duration-200 flex flex-col items-center text-center cursor-pointer"
                >
                  <span className="text-2xl mb-2 group-hover:scale-110 transition-transform">📁</span>
                  <span className="font-bold text-sm">Entire Folder / SD Card</span>
                  <span className="text-[11px] text-gray-500 group-hover:text-amber-100 mt-1">Select whole folder</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="group p-5 rounded-2xl bg-white hover:bg-slate-800 border border-gray-200 hover:border-slate-800 text-gray-900 hover:text-white shadow-xs hover:shadow-lg transition-all duration-200 flex flex-col items-center text-center cursor-pointer"
                >
                  <span className="text-2xl mb-2 group-hover:scale-110 transition-transform">🖼️</span>
                  <span className="font-bold text-sm">Select Photos Manually</span>
                  <span className="text-[11px] text-gray-500 group-hover:text-gray-200 mt-1">Individual image files</span>
                </button>
              </div>
            )}

            {selectedFiles.length > 0 && !isExtractingZip && (
              <div className="mt-8 inline-flex flex-wrap items-center justify-center gap-4 bg-white px-6 py-4 rounded-2xl border border-orange-300 shadow-md">
                <div className="text-left">
                  <p className="text-xs font-bold text-gray-900">
                    📁 {selectedFiles.length.toLocaleString()} photos selected
                  </p>
                  <p className="text-[11px] text-gray-500">Ready to upload to {selectedAlbum?.title}</p>
                </div>
                <button
                  type="button"
                  onClick={startBulkUpload}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 text-white font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                >
                  {isAdminUser ? 'Start Upload & Indexing 🚀' : 'Start Bulk Upload 🚀'}
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Live Progress & Speed/ETA Dashboard */
          <div className="bg-gradient-to-br from-slate-900 via-gray-900 to-slate-950 rounded-3xl p-6 sm:p-10 border border-gray-800 text-white shadow-xl space-y-6">
            
            {/* Top Status & Live Speed Badge */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-orange-400 uppercase tracking-wider">
                  Live Multi-Threaded Ingestion
                </span>
                <h3 className="text-base sm:text-lg font-bold text-white mt-0.5">
                  {currentStepText || 'Streaming photos to AWS S3 in parallel...'}
                </h3>
              </div>

              {/* Speed & ETA Pill */}
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 px-3.5 py-1.5 rounded-full text-xs font-bold text-orange-300 shadow-inner">
                <Zap className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
                <span>{uploadSpeed || 'Calculating...'}</span>
                <span className="text-gray-500">&bull;</span>
                <Clock className="w-3.5 h-3.5 text-orange-300" />
                <span>{uploadEta || 'Estimating...'}</span>
              </div>
            </div>

            {/* Progress Bar with Glow */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>Progress</span>
                <span className="font-extrabold text-orange-400 text-sm">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-5 overflow-hidden p-0.5 border border-gray-700 shadow-inner">
                <div
                  className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400 h-full rounded-full transition-all duration-300 shadow-lg"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>

            {/* 3 Live Metric Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                <span className="text-[11px] uppercase font-bold text-gray-400 block mb-1">
                  Photos Processed
                </span>
                <span className="text-lg font-black text-white">
                  {processedCount.toLocaleString()}
                </span>
                <span className="text-xs text-gray-400"> / {totalFilesToUpload.toLocaleString()}</span>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                <span className="text-[11px] uppercase font-bold text-gray-400 block mb-1">
                  Transfer Speed & Data
                </span>
                <span className="text-lg font-black text-yellow-400">
                  {uploadSpeed || '-- MB/s'}
                </span>
                <span className="text-xs text-gray-400 block mt-0.5">
                  {uploadedDataFormatted || 'Direct to AWS S3'}
                </span>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                <span className="text-[11px] uppercase font-bold text-gray-400 block mb-1">
                  Estimated Time Left
                </span>
                <span className="text-lg font-black text-orange-400">
                  {uploadEta || '--'}
                </span>
                <span className="text-xs text-gray-400 block mt-0.5">
                  {isAdminUser ? `${indexedFacesCount.toLocaleString()} Faces Indexed` : 'Auto-Indexed'}
                </span>
              </div>
            </div>

            {/* Live Ingestion Filmstrip */}
            {recentThumbnails.length > 0 && (
              <div className="border-t border-gray-800 pt-4">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">
                  Live Cloud Ingestion Stream
                </span>
                <div className="flex items-center gap-2.5 overflow-x-auto pb-1">
                  {recentThumbnails.map((thumb, idx) => (
                    <div key={idx} className="relative w-14 h-14 rounded-xl overflow-hidden border border-gray-700 flex-shrink-0 bg-gray-800 shadow-xs">
                      <img
                        src={thumb.photoUrl}
                        alt="Uploaded"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="absolute bottom-1 right-1 w-3.5 h-3.5 rounded-full bg-green-500 text-white flex items-center justify-center text-[9px] shadow-xs">
                        ✓
                      </span>
                    </div>
                  ))}
                  <div className="w-14 h-14 rounded-xl border border-dashed border-gray-700 flex items-center justify-center text-gray-500 text-xs flex-shrink-0">
                    <RefreshCw className="w-4 h-4 animate-spin text-orange-400" />
                  </div>
                </div>
              </div>
            )}

            {/* Safety Warning */}
            <div className="text-center text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-xl py-2 px-3">
              🛡️ Live batch upload in progress. Please keep this browser window open.
            </div>
          </div>
        )}
      </div>

      {/* New Album Modal (Admin Only) */}
      {isAdminUser && isNewAlbumModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative">
            <button
              onClick={() => setIsNewAlbumModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
              <FolderPlus className="w-6 h-6" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-1">Create Event Album</h3>
            <p className="text-xs text-gray-500 mb-5">
              Initializes S3 storage and a dedicated AWS Rekognition facial collection for this event.
            </p>

            <form onSubmit={handleCreateAlbum} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Event Album Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newAlbumTitle}
                  onChange={(e) => setNewAlbumTitle(e.target.value)}
                  placeholder="e.g. Diwali Dhol Tasha 2026"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event Date</label>
                <input
                  type="date"
                  value={newAlbumDate}
                  onChange={(e) => setNewAlbumDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                <textarea
                  rows="2"
                  value={newAlbumDescription}
                  onChange={(e) => setNewAlbumDescription(e.target.value)}
                  placeholder="e.g. Photos from the grand procession and cultural stage."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsNewAlbumModalOpen(false)}
                  className="w-1/2 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingAlbum}
                  className="w-1/2 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:opacity-50 shadow-md"
                >
                  {isCreatingAlbum ? 'Creating...' : 'Create Album'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoRecognitionManagement;
